import { MAX_MAP_ZOOM } from "../../../ShiftDiscovery/MapView/constants";
import { getBoundsAroundLatLng } from "../../../ShiftDiscovery/MapView/WorkplaceClusterer/getBoundsAroundLatLng";
import type { VisitLocation, VisitLocationCluster, VisitLocationMarker } from "./types";

const MARKER_WIDTH = 40;
const MARKER_HEIGHT = 40;

function buildClusterId(markers: VisitLocationMarker[]) {
  return markers
    .map((marker) => marker.visitLocation.id)
    .sort()
    .join("-");
}

function calculateMarkerBounds(marker: VisitLocationMarker, map: google.maps.Map) {
  return getBoundsAroundLatLng(map, marker.position, MARKER_WIDTH, MARKER_HEIGHT);
}

function calculateClusterBounds(cluster: VisitLocationCluster, map: google.maps.Map) {
  const clusterBounds = new google.maps.LatLngBounds();
  cluster.markers.forEach((marker) => {
    const bounds = calculateMarkerBounds(marker, map);
    if (bounds) {
      clusterBounds.union(bounds);
    }
  });
  return clusterBounds;
}

interface BuildVisitLocationClustersProps {
  map: google.maps.Map;
  visitLocations: VisitLocation[];
}

export function buildVisitLocationClusters(
  props: BuildVisitLocationClustersProps
): VisitLocationCluster[] {
  const { map, visitLocations } = props;
  const currentZoom = map.getZoom() ?? 0;

  const shouldCluster = currentZoom < MAX_MAP_ZOOM;

  const visitLocationMarkers: VisitLocationMarker[] = visitLocations.map((visitLocation) => {
    const position = new google.maps.LatLng(
      visitLocation.location.latitude,
      visitLocation.location.longitude
    );
    const bounds = calculateMarkerBounds({ visitLocation, position }, map);
    return { visitLocation, position, bounds };
  });

  const clusters: VisitLocationCluster[] = [];

  visitLocationMarkers.forEach((marker) => {
    const { bounds, position, visitLocation } = marker;

    if (!bounds || !shouldCluster) {
      clusters.push({
        id: buildClusterId([marker]),
        position,
        bounds,
        markers: [marker],
        totalVisits: visitLocation.visits.length,
      });
      return;
    }

    const existingCluster = clusters.find((cluster) => cluster.bounds?.intersects(bounds));

    if (existingCluster) {
      existingCluster.markers.push(marker);
      existingCluster.totalVisits += visitLocation.visits.length;
      existingCluster.bounds = calculateClusterBounds(existingCluster, map);
      existingCluster.position = existingCluster.bounds.getCenter();
      existingCluster.id = buildClusterId(existingCluster.markers);
      return;
    }

    clusters.push({
      id: buildClusterId([marker]),
      position,
      bounds,
      markers: [marker],
      totalVisits: visitLocation.visits.length,
    });
  });

  return clusters.sort((a, b) => a.totalVisits - b.totalVisits);
}
